import React, { useState, useEffect } from "react";
import { Container, Row, Col, Card, Form, Button, Spinner, Badge, Alert, Modal } from "react-bootstrap";
import axios from "axios";
import { generateVolunteersPDF, generateVolunteersCSV } from "../../../../utils/pdfUtils";
import { Download } from "lucide-react";
import { API_ENDPOINTS } from "../../../../config/api";

const AllVolunteers = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [error, setError] = useState("");
  const [activeTab, setActiveTab] = useState("active"); // "active" or "inactive"
  // Status change modal state
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [selectedVolunteer, setSelectedVolunteer] = useState(null);
  const [statusReason, setStatusReason] = useState("");
  const [reasonPreset, setReasonPreset] = useState("");
  const [zoomedPhoto, setZoomedPhoto] = useState(null);

  const openStatusModal = (volunteer) => {
    setSelectedVolunteer(volunteer);
    setReasonPreset("");
    setStatusReason("");
    setShowStatusModal(true);
  };

  const closeStatusModal = () => {
    setShowStatusModal(false);
    setSelectedVolunteer(null);
    setStatusReason("");
    setReasonPreset("");
  };

  const confirmStatusChange = async () => {
    const finalReason = reasonPreset === "Other" ? statusReason.trim() : reasonPreset;

    if (!finalReason) {
      alert("Please provide a reason.");
      return;
    }

    try {
      const res = await axios.patch(
        API_ENDPOINTS.VOLUNTEERS.TOGGLE_AVAILABILITY(selectedVolunteer.id),
        { reason: finalReason, changedBy: "admin" }
      );
      setUsers((prev) =>
        prev.map((u) => u.id === selectedVolunteer.id ? { ...u, isAvailable: res.data.isAvailable } : u)
      );
      closeStatusModal();
    } catch (err) {
      console.error("Failed to update status", err);
      alert("Failed to update status. Please try again.");
    }
  };

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const response = await axios.get(API_ENDPOINTS.VOLUNTEERS.GET_ALL);
      setUsers(response.data);
    } catch (err) {
      setError("Failed to load volunteers");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const filteredUsers = users.filter(user => {
    const matchesSearch =
      user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = activeTab === "active" ? user.isAvailable === true : user.isAvailable === false;
    return matchesSearch && matchesStatus;
  });
  const handleDownloadCSV = () => {
    if (filteredUsers.length === 0) {
      alert('No volunteers available to download');
      return;
    }
    const statusLabel = activeTab === "active" ? "Active Volunteers" : "Inactive Volunteers";
    generateVolunteersCSV(filteredUsers, statusLabel);
  };

  const handleDownloadPDF = () => {
    if (filteredUsers.length === 0) {
      alert('No volunteers available to download');
      return;
    }
    const statusLabel = activeTab === "active" ? "Active Volunteers" : "Inactive Volunteers";
    generateVolunteersPDF(filteredUsers, statusLabel);
  };

  return (
    <div className="main-content">
    <Container className="px-4 py-3" style={{ minHeight: '100vh' }}>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h2 className="mb-0" style={{ color: '#a70a4a', fontWeight: '600' }}>Volunteer Management</h2>
          <p className="mb-0" style={{ color: '#7f8c9b', fontSize: '0.85rem' }}>Manage your volunteer community</p>
        </div>
      </div>

      <Row className="g-3 mb-4">
        <Col xs={12} md={4}>
          <div className="db1-stat-card">
            <div className="text-center">
              <h6 className="mb-1" style={{ color: '#7f8c9b', fontSize: '0.82rem', fontWeight: 500 }}>Total Volunteers</h6>
              <h3 style={{ color: '#ac2e62', fontWeight: '700' }}>{users.length}</h3>
            </div>
          </div>
        </Col>
        <Col xs={12} md={4}>
          <div className="db1-stat-card">
            <div className="text-center">
              <h6 className="mb-1" style={{ color: '#7f8c9b', fontSize: '0.82rem', fontWeight: 500 }}>Active</h6>
              <h3 style={{ color: '#16a34a', fontWeight: '700' }}>
                {users.filter(u => u.isAvailable === true).length}
              </h3>
            </div>
          </div>
        </Col>
        <Col xs={12} md={4}>
          <div className="db1-stat-card">
            <div className="text-center">
              <h6 className="mb-1" style={{ color: '#7f8c9b', fontSize: '0.82rem', fontWeight: 500 }}>Inactive</h6>
              <h3 style={{ color: '#dc2626', fontWeight: '700' }}>
                {users.filter(u => u.isAvailable === false).length}
              </h3>
            </div>
          </div>
        </Col>
      </Row>

      <div className="d-flex flex-wrap justify-content-between align-items-center mb-4 gap-2">
        <Form.Control
          type="text"
          placeholder="Search volunteers..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="db1-search"
          style={{ width: '300px' }}
        />

        <div className="d-flex align-items-center gap-2">
          <button
            onClick={handleDownloadPDF}
            className="db1-export-btn"
            style={{ background: '#28a745', color: 'white' }}
            title="Download Volunteers as PDF"
          >
            <Download size={16} />
            Download PDF
          </button>

          <button
            onClick={handleDownloadCSV}
            className="db1-export-btn"
            style={{ background: '#ac2e62', color: 'white' }}
            title="Download Volunteers as CSV"
          >
            <Download size={16} />
            Download CSV
          </button>

          <div className="btn-group" role="group">
            <button
              onClick={() => setActiveTab("active")}
              className={`db1-period-btn ${activeTab === "active" ? 'active' : ''}`}
              style={{
                background: activeTab === "active" ? '#16a34a' : 'transparent',
                color: activeTab === "active" ? 'white' : '#16a34a',
                border: '1px solid #16a34a'
              }}
            >
              Active
            </button>

            <button
              onClick={() => setActiveTab("inactive")}
              className={`db1-period-btn ${activeTab === "inactive" ? 'active' : ''}`}
              style={{
                background: activeTab === "inactive" ? '#dc2626' : 'transparent',
                color: activeTab === "inactive" ? 'white' : '#dc2626',
                border: '1px solid #dc2626'
              }}
            >
              Inactive
            </button>
          </div>
        </div>
      </div>

      {error && (
        <Alert variant="danger" className="mb-4">
          {error}
        </Alert>
      )}

      {loading ? (
        <div className="text-center py-5">
          <Spinner animation="border" style={{ color: '#ac2e62' }} />
          <p className="mt-3" style={{ color: '#7f8c9b' }}>Loading volunteers...</p>
        </div>
      ) : filteredUsers.length === 0 ? (
        <div className="db1-empty-state" style={{ padding: '60px 20px', textAlign: 'center' }}>
          <h5 style={{ color: '#2c3e50' }}>No volunteers found</h5>
          <p style={{ color: '#7f8c9b' }}>
            {searchTerm ? "Try a different search term" : "No volunteers in this category"}
          </p>
        </div>
      ) : (
        <Row xs={1} md={2} lg={3} className="g-4">
          {filteredUsers.map((user) => (
            <Col key={user.id}>
              <div className="db1-glass-card" style={{ padding: 0, overflow: 'hidden' }}>
                <div style={{ padding: '1.25rem' }}>
                  <div className="text-center mb-3">
                    {user.profilePhoto ? (
                      <img
                        src={user.profilePhoto}
                        alt={user.name}
                        onClick={() => setZoomedPhoto(user.profilePhoto)}
                        style={{ width: 70, height: 70, borderRadius: '16px', objectFit: 'cover', border: '3px solid #ac2e62', cursor: 'zoom-in' }}
                      />
                    ) : (
                      <div className="db1-avatar" style={{ width: 70, height: 70, borderRadius: '16px', fontSize: '1.5rem', margin: '0 auto' }}>
                        {user.name?.charAt(0) || '?'}
                      </div>
                    )}
                  </div>
                  <h5 style={{ color: '#2c3e50', fontWeight: 600, textAlign: 'center' }}>{user.name}</h5>
                  <p className="text-center mb-3" style={{ color: '#7f8c9b', fontSize: '0.8rem' }}>
                    ID: {user.virtualId}
                  </p>

                  <div style={{ fontSize: '0.85rem' }}>
                    <div className="d-flex align-items-center mb-2">
                      <i className="bi bi-envelope me-2" style={{ color: '#7f8c9b', width: '20px' }}></i>
                      <span style={{ color: '#2c3e50' }}>{user.email}</span>
                    </div>
                    <div className="d-flex align-items-center mb-2">
                      <i className="bi bi-telephone me-2" style={{ color: '#7f8c9b', width: '20px' }}></i>
                      <span style={{ color: '#2c3e50' }}>{user.mobileNo}</span>
                    </div>
                    <div className="d-flex align-items-center">
                      <i className="bi bi-geo-alt me-2" style={{ color: '#7f8c9b', width: '20px' }}></i>
                      <span style={{ color: '#2c3e50' }}>{user.address}</span>
                    </div>
                  </div>
                </div>
                <div style={{
                  borderTop: '1px solid rgba(0,0,0,0.04)',
                  padding: '12px 16px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}>
                  <span className={`db1-badge ${user.isAvailable ? 'active' : 'inactive'}`}>
                    {user.isAvailable ? "● Active" : "● Inactive"}
                  </span>
                  <button
                    onClick={() => openStatusModal(user)}
                    className="db1-toggle-btn"
                    style={{
                      background: user.isAvailable ? 'rgba(220,38,38,0.08)' : 'rgba(22,163,74,0.08)',
                      color: user.isAvailable ? '#dc2626' : '#16a34a',
                    }}
                  >
                    Mark as {user.isAvailable ? "Inactive" : "Active"}
                  </button>
                </div>
              </div>
            </Col>
          ))}
        </Row>
      )}
    </Container>

    <Modal show={!!zoomedPhoto} onHide={() => setZoomedPhoto(null)} centered size="sm">
      <Modal.Body className="text-center p-2" style={{ background: '#000' }}>
        <img
          src={zoomedPhoto}
          alt="Profile"
          style={{ width: '100%', borderRadius: '8px', maxHeight: '80vh', objectFit: 'contain' }}
        />
      </Modal.Body>
    </Modal>

    <Modal show={showStatusModal} onHide={closeStatusModal} centered>
      <div className="db1-modal-header">
        <h5 className="mb-0" style={{ color: '#2c3e50' }}>
          {selectedVolunteer?.isAvailable ? "Mark as Inactive" : "Mark as Active"}
        </h5>
        <button onClick={closeStatusModal} className="btn-close" style={{ color: '#a70a4a' }}></button>
      </div>
      <div className="db1-modal-content">
        <p style={{ color: '#2c3e50' }}>
          <strong>{selectedVolunteer?.name}</strong> will be marked as{" "}
          <strong>{selectedVolunteer?.isAvailable ? "Inactive" : "Active"}</strong>.
        </p>
        <Form.Group className="mb-3">
          <Form.Label style={{ color: '#2c3e50', fontWeight: 600 }}>Reason</Form.Label>
          <Form.Select value={reasonPreset} onChange={(e) => setReasonPreset(e.target.value)}>
            <option value="">-- Select a reason --</option>
            {selectedVolunteer?.isAvailable ? (
              <>
                <option value="Completed Internship/Volunteering Term">Completed Internship/Volunteering Term</option>
                <option value="Resigned">Resigned</option>
                <option value="Inactive / Unreachable">Inactive / Unreachable</option>
                <option value="Other">Other</option>
              </>
            ) : (
              <>
                <option value="Rejoined NGO">Rejoined NGO</option>
                <option value="Reactivated after review">Reactivated after review</option>
                <option value="Other">Other</option>
              </>
            )}
          </Form.Select>
        </Form.Group>
        {reasonPreset === "Other" && (
          <Form.Group className="mb-3">
            <Form.Label style={{ color: '#2c3e50', fontWeight: 600 }}>Please specify</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              value={statusReason}
              onChange={(e) => setStatusReason(e.target.value)}
              placeholder="Enter reason..."
            />
          </Form.Group>
        )}
      </div>
      <div style={{ padding: '1rem 1.5rem', borderTop: '1px solid rgba(0,0,0,0.04)', display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
        <button onClick={closeStatusModal} className="db1-delete-btn">Cancel</button>
        <button
          onClick={confirmStatusChange}
          className="db1-export-btn"
          style={{
            background: selectedVolunteer?.isAvailable ? '#dc2626' : '#16a34a',
            color: 'white'
          }}
        >
          Confirm
        </button>
      </div>
    </Modal>
    </div>
  );
};

export default AllVolunteers;